import { Banner } from '@components';
import { useFormikContext } from 'formik';
import type { FC } from 'react';

import type { UserReminderFormikType } from '@core/formik/userReminder';
import Enums from '@core/types/api/Enums';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { reminderBusinessDateResolver } from '@core/utils/reminderRecordTypes';
import { BUSINESS_DATE_MISSING_ERROR, reminderTriggerDate } from '@core/utils/reminders';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

import { ReminderTriggerHintBanner } from './ReminderTriggerHintBanner';

export const ReminderTriggerHint: FC = () => {
  const { values } = useFormikContext<UserReminderFormikType>();
  const { recordType, recordId, recordBusinessDate, notifyBefore } = values;

  const isProject = recordType === Enums.ReminderUserSettingsRecordType.Project;
  const isActivity = recordType === Enums.ReminderUserSettingsRecordType.Activity;
  const hasRecord = hasStringValue(recordId);

  const { data: project, isLoading: isLoadingProject } = useSlabQuery(
    'GET project by ID',
    { pathParams: { id: recordId } },
    { enabled: isProject && hasRecord },
  );

  const { data: quote, isLoading: isLoadingQuote } = useSlabQuery(
    'GET quote by ID',
    { pathParams: { id: recordId } },
    { enabled: recordType === Enums.ReminderUserSettingsRecordType.Quote && hasRecord },
  );

  const { data: activity, isLoading: isLoadingActivity } = useSlabQuery(
    'GET activity by ID',
    { pathParams: { id: recordId } },
    { enabled: isActivity && hasRecord },
  );

  if (!hasRecord || recordBusinessDate === Enums.ReminderUserSettingsBusinessDate.General) {
    return null;
  }

  if (isLoadingProject || isLoadingQuote || isLoadingActivity) {
    return null;
  }

  let record: { id: string } | undefined = quote;
  if (isProject) {
    record = project;
  }
  if (isActivity) {
    record = activity;
  }
  // A cache hit for the previously selected record resolves before the new one
  // loads, so its date would be shown against the record now in the form.
  if (!hasValue(record) || record.id !== recordId) {
    return null;
  }

  const resolveBusinessDate = reminderBusinessDateResolver(recordType, recordBusinessDate);
  if (!hasValue(resolveBusinessDate)) {
    return null;
  }

  const businessDate = resolveBusinessDate({
    project,
    quote,
    activity: hasValue(activity) ? { ...activity, activityCategory: activity.category } : undefined,
  });
  if (!hasValue(businessDate)) {
    return <Banner severity='warning'>{BUSINESS_DATE_MISSING_ERROR}</Banner>;
  }

  const triggerDate = reminderTriggerDate(businessDate, notifyBefore ?? '', !isActivity);
  if (!hasValue(triggerDate)) {
    return null;
  }

  return <ReminderTriggerHintBanner triggerDate={triggerDate} />;
};
